import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumVesselStateIcon } from "./enums/enumVesselStateIcon.generated";

// Model Objects

export class VesselState extends ModelObjectWithDirtyCheck<VesselState> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Vessel State Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    public isHidden: boolean = false;
    /**
    * Vessel Icon Type
    */
    public icon: EnumVesselStateIcon = EnumVesselStateIcon.none;
    /**
    * Vessel State Description
    * Not Nullable
    */
    public description: string = "";
    /**
    * Vessel State Colour
    * Not Nullable
    */
    public colour: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.isHidden = jsonSerializedObject.isHidden;
          this.icon = EnumVesselStateIcon.getByValue(jsonSerializedObject.icon, this.icon);
          this.description = jsonSerializedObject.description;
          this.colour = jsonSerializedObject.colour;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.isHidden = this.isHidden;
        toRet.icon = this.icon.value;
        toRet.description = this.description;
        toRet.colour = this.colour;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "VesselState.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
    ];
}


export class VesselStateService extends ServiceBase {

  public async getVesselStates(vesselStateIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(VesselState)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    vesselStateIds:vesselStateIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.vesselStates.map((v: any) => new VesselState(v));;
    return this.makeJsonRequest("vesselState/v1/getVesselStates", req, false, extractor, opts);
  };

  public async getAllVesselStates(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(VesselState)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.vesselStates.map((v: any) => new VesselState(v));;
    return this.makeJsonRequest("vesselState/v1/getAllVesselStates", req, false, extractor, opts);
  };

  public async saveVesselState(vesselState: VesselState, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    vesselState:vesselState.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("vesselState/v1/saveVesselState", req, true, extractor, opts);
  };

  public async setVesselStateIsHidden(vesselStateId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    vesselStateId:vesselStateId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("vesselState/v1/setVesselStateIsHidden", req, true, extractor, opts);
  };
}

export const vesselStateService = new VesselStateService();